import { YMaps, Map } from "@pbe/react-yandex-maps";
import styles from "./styles.module.scss";
import { useEffect, useRef, useState } from "react";
import clsx from "clsx";

export type MapItem = {
  id: number;
  selected: boolean;
  coords: [number, number];
};

type ItemsYmapProps = {
  items: MapItem[];
  onItemClick?: (item: MapItem) => void;
  zoom?: number;
  isSetCenterByFirstItem?: boolean;
  isDisableZoomControl?: boolean;
  ownBehaviors?: string[];
};

const KRSK_COORDS = [56.023097, 92.874172];

let HOVER_TIMEOUT: NodeJS.Timeout | undefined;
let IS_PHONE = window.innerWidth < 800;

interface IPoint {
  id: number;
  type: "Feature";
  geometry: {
    type: "Point";
    coordinates: [number, number];
  };
  options: {
    preset: string;
  };
  properties: any;
}

const ItemsYmap: React.FC<ItemsYmapProps> = (props) => {
  const {
    items,
    zoom,
    isSetCenterByFirstItem,
    isDisableZoomControl,
    ownBehaviors = [],
    onItemClick,
  } = props;

  const [isHoverTouch, setIsHoverTouch] = useState(true);

  const handleTouchMove = (event: React.TouchEvent<HTMLDivElement>) => {
    if (event?.targetTouches.length === 1) {
      if (!isHoverTouch) {
        if (HOVER_TIMEOUT) {
        } else {
          HOVER_TIMEOUT = setTimeout(() => {
            setIsHoverTouch(true);
          }, 200);
        }
      }
    } else {
      clearTimeout(HOVER_TIMEOUT);
      HOVER_TIMEOUT = undefined;
      if (isHoverTouch) {
        setIsHoverTouch(false);
      }
    }
  };

  const handleTouchEnd = (event: React.TouchEvent<HTMLDivElement>) => {
    clearTimeout(HOVER_TIMEOUT);
    HOVER_TIMEOUT = undefined;
    if (isHoverTouch) {
      setIsHoverTouch(false);
    }
  };

  const mapRef = useRef<any>(null);
  const [mapInstance, setMapInstance] = useState<any>(null);

  const handleClick = (item: MapItem) => {
    onItemClick && onItemClick(item);
  };

  useEffect(() => {
    if (mapInstance) {
      const checkedPoints: number[] = [];
      const objectManager = new mapInstance.ObjectManager();

      const createPoint = (mapItem: MapItem, selectedPointId?: number) => {
        return {
          type: "Feature",
          geometry: {
            type: "Point",
            coordinates: mapItem.coords,
          },
          options: {
            preset:
              selectedPointId === mapItem.id
                ? "islands#blueCircleDotIcon"
                : selectedPointId && checkedPoints.includes(mapItem.id)
                ? "islands#darkGreenCircleIcon"
                : "islands#blueCircleIcon",
          },
          properties: {},
          id: mapItem.id,
        } as IPoint;
      };

      const createPoints = (mapPoints: MapItem[], selectedPointId?: number) => {
        return mapPoints.map((point) => {
          return createPoint(point, selectedPointId);
        });
      };

      const deletePoint = (pointId: number) => {
        objectManager.remove([pointId]);
      };

      const setPoints = (points: IPoint[]) => {
        objectManager.add({
          type: "FeatureCollection",
          features: points,
        });

        mapRef.current.geoObjects.add(objectManager);
      };

      setPoints(createPoints(items));

      objectManager.events.add(["click"], (event: any) => {
        const pointId = event.get("objectId");
        const point = items.find((item) => item.id === pointId);
        if (point) {
          handleClick(point);

          checkedPoints?.push(point.id);

          deletePoint(point.id);

          checkedPoints?.forEach((checkedPoint) => {
            deletePoint(checkedPoint);
          });

          setPoints(createPoints(items, point.id));
        }
      });

      const removeGeoObjects = () => {
        objectManager.removeAll();
      };

      return () => {
        removeGeoObjects();
      };
    }
  }, [JSON.stringify(items), mapInstance]);

  return (
    <div
      className={styles.yandexMap}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {IS_PHONE && (
        <div
          className={clsx(
            styles.yandexMap__hint,
            isHoverTouch && styles.yandexMap__hint_hover
          )}
        >
          <div className={styles.yandexMap__text}>
            Чтобы переместить карту проведите по ней двумя пальцами
          </div>
        </div>
      )}
      <YMaps>
        <Map
          modules={["control.ZoomControl", "ObjectManager"]}
          className={styles.yandexMap__map}
          state={{
            center: isSetCenterByFirstItem ? items[0].coords : KRSK_COORDS,
            zoom: zoom ? zoom : 11,
            controls: ["zoomControl"],
            behaviors: ownBehaviors.length
              ? ownBehaviors
              : isDisableZoomControl
              ? ["disable('scrollZoom')", "multiTouch"]
              : undefined,
          }}
          instanceRef={mapRef}
          onLoad={(ymap) => {
            setMapInstance(ymap);
          }}
        />
      </YMaps>
    </div>
  );
};

export default ItemsYmap;
